
import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import backgroundImage from '../background.webp';
import companionBorder from '../companionborder.png';
import CompanionAnswerButton from './CompanionAnswerButton';
import buttonImage from '../buttonimage.webp'; // Add this import

// Visually hidden style for accessibility
const visuallyHidden = {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: '1px',
    margin: '-1px',
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    width: '1px',
    whiteSpace: 'nowrap',
};

const Companion = ({ goToPage, goBack, goHome }) => {
    const [currentQuestionId, setCurrentQuestionId] = useState(null);
    const [bubbleHistory, setBubbleHistory] = useState([]);
    const [historyStack, setHistoryStack] = useState([]);
    const [loading, setLoading] = useState(true);
    const [resumeHandled, setResumeHandled] = useState(false);
    const chatContainerRef = useRef(null);
    const hasFetched = useRef(false);
    const resumeQuestionId = typeof window !== 'undefined' ? localStorage.getItem('resumeQuestionId') : null;

    useEffect(() => {
        // Only handle resume if resumeQuestionId is set and not yet handled
        if (
            resumeQuestionId &&
            resumeQuestionId !== "null" &&
            resumeQuestionId !== "" &&
            !resumeHandled
        ) {
            const doResume = async () => {
                setLoading(true);
                try {
                    const { data: { user }, error: userError } = await supabase.auth.getUser();
                    if (userError || !user) {
                        alert('User not logged in!');
                        setResumeHandled(true);
                        setLoading(false);
                        return;
                    }
                    const { data: profile } = await supabase
                        .from('user_profiles')
                        .select('nickname, first_name')
                        .eq('id', user.id)
                        .single();
                    const userNickname = profile?.nickname || profile?.first_name || 'User';

                    const { data: savedQuestion, error: resumeError } = await supabase
                        .from('questions4')
                        .select('*')
                        .eq('id', resumeQuestionId)
                        .maybeSingle();

                    if (resumeError) {
                        alert('Failed to load the resumed question: ' + resumeError.message);
                        setResumeHandled(true);
                        setLoading(false);
                        return;
                    }
                    if (!savedQuestion) {
                        localStorage.removeItem('resumeQuestionId');
                        setResumeHandled(true);
                        setLoading(false);
                    } else {
                        setCurrentQuestionId(savedQuestion.id);
                        setBubbleHistory([
                            { type: 'greeting', text: `Hey, ${userNickname}!`, id: 'greeting-' + Date.now() },
                            { type: 'question', text: savedQuestion.text, id: 'question-' + savedQuestion.id + '-' + Date.now(), questionId: savedQuestion.id }
                        ]);
                        setHistoryStack([{ questionId: savedQuestion.id, bubbleHistory: [] }]);
                        fetchAnswers(savedQuestion.id);
                        localStorage.removeItem('resumeQuestionId');
                        setResumeHandled(true);
                        setLoading(false);
                    }
                } catch {
                    setResumeHandled(true);
                    setLoading(false);
                }
            };
            doResume();
        }
    // eslint-disable-next-line
    }, [resumeQuestionId, resumeHandled]);

    useEffect(() => {
        // Only run normal flow if not resuming and chat is not already set up
        if (
            (resumeQuestionId && resumeQuestionId !== "null" && resumeQuestionId !== "" && !resumeHandled) ||
            bubbleHistory.length > 0
        ) {
            return;
        }
        if (hasFetched.current) return;
        hasFetched.current = true;

        const fetchData = async () => {
            try {
                setLoading(true);

                const { data: { user }, error: userError } = await supabase.auth.getUser();
                if (userError || !user) {
                    console.error('Error fetching user:', userError);
                    alert('User not logged in!');
                    return;
                }

                // eslint-disable-next-line no-unused-vars
                const { data: profile, error: profileError } = await supabase
                    .from('user_profiles')
                    .select('nickname, first_name')
                    .eq('id', user.id)
                    .single();

                if (profileError) {
                    console.error('Error fetching profile:', profileError.message);
                    alert('Failed to load user profile.');
                    return;
                }

                const userNickname = profile.nickname || profile.first_name || 'User';

                const { data: question, error: questionError } = await supabase
                    .from('questions4')
                    .select('*')
                    .eq('is_first', true)
                    .single();

                if (questionError) {
                    console.error('Error fetching first question:', questionError.message);
                    alert('Failed to load the first question.');
                    return;
                }

                setCurrentQuestionId(question.id);
                setTimeout(() => {
                    setBubbleHistory([{ type: 'greeting', text: `Hey, ${userNickname}!`, id: 'greeting-' + Date.now() }]);
                }, 1000);

                setTimeout(() => {
                    setBubbleHistory((prev) => [
                        ...prev,
                        { type: 'question', text: question.text, id: 'question-' + question.id + '-' + Date.now(), questionId: question.id },
                    ]);
                    setHistoryStack([{ questionId: question.id, bubbleHistory: [...bubbleHistory] }]);
                }, 2000);

                setTimeout(() => {
                    fetchAnswers(question.id);
                    sessionStorage.setItem('firstQuestionId', question.id.toString());
                }, 3000);
            } catch (err) {
                console.error('Unexpected error:', err);
                alert('An unexpected error occurred.');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    // eslint-disable-next-line
    }, [resumeQuestionId, resumeHandled, goToPage, bubbleHistory]);
    
    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [bubbleHistory]);

    const fetchAnswers = async (questionId) => {
        try {
            const { data: fetchedAnswers, error } = await supabase
                .from('answers4')
                .select('*')
                .eq('question_id', questionId);

            if (error) {
                console.error('Error fetching answers4:', error.message);
                alert('Failed to load answers4.');
                return;
            }

            setBubbleHistory((prev) => {
                if (prev.some((bubble) => bubble.type === 'answerOptions' && bubble.id.includes(`options-${questionId}`))) {
                    return prev;
                }
                return prev.concat({
                    type: 'answerOptions',
                    answers4: fetchedAnswers,
                    id: 'options-' + questionId + '-' + Date.now(),
                });
            });
        } catch (err) {
            console.error('Unexpected error fetching answers4:', err);
            alert('An unexpected error occurred while fetching answers4.');
        }
    };

    const navigateTo = (component) => {
        if (goToPage) {
            goToPage(component);
        } else {
            console.error('goToPage is not available');
            alert('Navigation error. Please try again.');
        }
    };

    const handleAnswerClick = async (answer) => {
        try {
            setHistoryStack((prev) => [...prev, { questionId: currentQuestionId, bubbleHistory: [...bubbleHistory] }]);

            setBubbleHistory((prev) =>
                prev.map((bubble) =>
                    bubble.type === 'answerOptions'
                        ? { type: 'answer', text: answer.text, id: answer.id }
                        : bubble
                )
            );

            setTimeout(async () => {
                if (answer.next_question_id) {
                    const { data: nextQuestion, error } = await supabase
                        .from('questions4')
                        .select('*')
                        .eq('id', answer.next_question_id)
                        .single();

                    if (error) {
                        console.error('Error fetching next question:', error.message);
                        alert('Failed to load next question.');
                        return;
                    }

                    setCurrentQuestionId(nextQuestion.id);
                    setBubbleHistory((prev) => [
                        ...prev,
                        { type: 'question', text: nextQuestion.text, id: 'question-' + nextQuestion.id + '-' + Date.now(), questionId: nextQuestion.id },
                    ]);
                    fetchAnswers(nextQuestion.id);
                } else if (answer.component) {
                    navigateTo(answer.component);
                } else {
                    // No action specified, preserve existing flow
                }
            }, 1000);
        } catch (err) {
            console.error('Answer error:', err);
            alert('An unexpected error occurred.');
        }
    };

    const handleBack = () => {
        if (historyStack.length > 1) {
            const newHistoryStack = [...historyStack];
            const lastEntry = newHistoryStack.pop();
            setHistoryStack(newHistoryStack);
            setCurrentQuestionId(lastEntry.questionId);
            setBubbleHistory(lastEntry.bubbleHistory);

            if (!lastEntry.bubbleHistory.some((bubble) => bubble.type === 'answerOptions' && bubble.id.includes(`options-${lastEntry.questionId}`))) {
                fetchAnswers(lastEntry.questionId);
            }
        } else if (goBack) {
            goBack(); // Use the passed goBack prop for navigation
        } else {
            console.error('No history to go back to and goBack prop is not available.');
        }
    };

    const handleSaveProgress = async () => {
        try {
            const { data: { user }, error: userError } = await supabase.auth.getUser();
            if (userError || !user) {
                console.error('Error fetching user:', userError);
                alert('User not logged in! Please log in to save progress.');
                return;
            }

            const { error: progressError } = await supabase
                .schema('api')
                .from('user_progress')
                .upsert({
                    user_id: user.id,
                    current_question_id: currentQuestionId,
                    current_component: 'companion',
                    animated_helper: 'no', // <-- ensure this is always set to 'no'
                    timestamp: new Date().toISOString(),
                });

            if (progressError) {
                console.error('Error saving progress:', progressError.message);
                alert('Failed to save progress: ' + progressError.message);
                return;
            }

            console.log('User progress saved successfully!');
            alert('Session progress has been saved successfully!');
        } catch (err) {
            console.error('Unexpected error saving progress:', err);
            alert('An unexpected error occurred while saving progress.');
        }
    };

    const isBrowser = () => {
        return typeof window !== 'undefined' && window.innerWidth > 600;
    };

    if (loading) return <div>Loading...</div>;

    function groupAnswers(answers4) {
        const rows = [];
        let currentRow = [];
        let currentLen = 0;
        answers4.forEach((answer) => {
            const len = answer.text.trim().length;
            if (len > 32) {
                if (currentRow.length) rows.push(currentRow);
                rows.push([answer]);
                currentRow = [];
                currentLen = 0;
            } else {
                if (currentLen + len + (currentRow.length > 0 ? 1 : 0) <= 32) {
                    currentRow.push(answer);
                    currentLen += len + (currentRow.length > 1 ? 1 : 0);
                } else {
                    if (currentRow.length) rows.push(currentRow);
                    currentRow = [answer];
                    currentLen = len;
                }
            }
        });
        if (currentRow.length) rows.push(currentRow);
        return rows;
    }

    return (
        <div style={{
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: 'cover',
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0',
            margin: '0',
        }}>
            {/* Visually hidden instructions for screen readers */}
            <div style={visuallyHidden} aria-live="polite">
              This is a simulated chat. Use Tab to navigate answer choices. Press Enter to select an answer. When you select an answer, a new question or a new page may appear.
            </div>
            <div
                ref={chatContainerRef}
                aria-live="polite"
                style={{
                    width: isBrowser() ? '50%' : '90%',
                    maxWidth: '360px',
                    height: isBrowser() ? '80vh' : '70vh',
                    margin: '0',
                    border: isBrowser() ? `10px solid transparent` : '2px solid #4B5563',
                    borderImage: isBrowser() ? `url(${companionBorder}) 300 50 300 50 stretch` : 'none',
                    borderRadius: '16px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    backgroundColor: 'white',
                    padding: '8px',
                    overflowY: 'auto', // <-- changed from 'hidden' to 'auto'
                    scrollBehavior: 'smooth',
                }}
            >
                {bubbleHistory.map((bubble) => (
                    <div
                        key={bubble.id}
                        style={{
                            display: 'flex',
                            justifyContent: bubble.type === 'question' || bubble.type === 'greeting' ? 'flex-end' : 'flex-start',
                            marginBottom: '16px',
                            flexWrap: 'wrap',
                            maxWidth: '100%',
                        }}
                    >
                        {bubble.type === 'answerOptions' ? (
                            <div
                                style={{
                                    backgroundColor: '#E6F0FA',
                                    padding: '10px',
                                    borderRadius: '10px',
                                    maxWidth: '80%',
                                }}
                            >
                                {groupAnswers(bubble.answers4).map((row, rowIdx) => (
                                    <div
                                        key={rowIdx}
                                        style={{
                                            display: 'flex',
                                            width: 'auto',
                                            marginBottom: rowIdx < bubble.answers4.length - 1 ? '4px' : 0,
                                            gap: 0,
                                        }}
                                    >
                                        {row.map((answer, idx) => (
                                            <div
                                                key={answer.id}
                                                style={{
                                                    flex: '0 0 auto',
                                                    display: 'flex',
                                                }}
                                            >
                                                <CompanionAnswerButton
                                                    text={answer.text}
                                                    onClick={() => handleAnswerClick(answer)}
                                                    ariaLabel={`Choose answer: ${answer.text}${answer.component ? '. This will take you to ' + answer.component + '.' : ''}`}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div style={{
                                backgroundColor: bubble.type === 'answer' ? '#E6F0FA' : '#007AFF',
                                color: bubble.type === 'answer' ? '#4B5563' : 'white',
                                padding: '10px',
                                borderRadius: '10px',
                                fontFamily: 'Times New Roman, Times, serif',
                                fontSize: '16px',
                                fontWeight: 'bold',
                                maxWidth: '80%',
                                wordWrap: 'break-word',
                                textAlign: bubble.type === 'answer' ? 'left' : 'right',
                            }}>
                                {bubble.text}
                            </div>
                        )}
                    </div>
                ))}
            </div>
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '16px',
                marginTop: '16px',
                flexWrap: 'wrap',
            }}>
                <button
                    style={{
                        backgroundImage: `url(${buttonImage})`, // Use buttonimage
                        backgroundSize: 'cover',
                        backgroundColor: 'white',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '10px 20px',
                        width: '120px',
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    }}
                    onClick={handleBack}
                >
                    Back
                </button>
                <button
                    style={{
                        backgroundImage: `url(${buttonImage})`, // Use buttonimage
                        backgroundSize: 'cover',
                        backgroundColor: 'white',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '10px 20px',
                        width: '120px',
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    }}
                    onClick={() => goToPage('home')}
                >
                    Home
                </button>
                <button
                    style={{
                        backgroundImage: `url(${buttonImage})`, // Use buttonimage
                        backgroundSize: 'cover',
                        backgroundColor: 'white',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '10px 20px',
                        width: '120px',
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    }}
                    onClick={handleSaveProgress}
                >
                    Save
                </button>
            </div>
        </div>
    );
};

export default Companion;